import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
    Plus,
    Search,
    Edit,
    Trash2,
    Eye,
    MapPin,
    Phone,
    Mail,
    User,
    Building,
    AlertCircle
} from 'lucide-react';
import Button from '../components/Button';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/Toast';

// Configuração do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface Proprietario {
    id: string;
    nome: string;
    estado: string;
    cidade: string;
    telefone?: string;
    email?: string;
    created_at: string;
    updated_at: string;
    imoveis_count?: number;
}

const Proprietarios: React.FC = () => {
    const [proprietarios, setProprietarios] = useState<Proprietario[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingProprietario, setEditingProprietario] = useState<Proprietario | null>(null);
    const [viewingProprietario, setViewingProprietario] = useState<Proprietario | null>(null);
    const [formData, setFormData] = useState({
        nome: '',
        estado: '',
        cidade: '',
        telefone: '',
        email: ''
    });
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalAnimating, setIsModalAnimating] = useState(false);
    const { toasts, showSuccess, showError } = useToast();

    // Estados do Brasil
    const estadosBrasil = [
        'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
        'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
        'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
    ];

    useEffect(() => {
        loadProprietarios();
        testSupabaseConnection();
    }, []);

    // Controlar animação do modal
    useEffect(() => {
        if (showModal) {
            // Pequeno delay para garantir que o modal seja renderizado antes da animação
            const timer = setTimeout(() => {
                setIsModalAnimating(true);
            }, 10);
            return () => clearTimeout(timer);
        }
    }, [showModal]);

    // Função para testar conexão e funções RPC
    const testSupabaseConnection = async () => {
        try {
            console.log('=== TESTE CONEXÃO SUPABASE ===');

            // Testar conexão básica
            const { data: authData, error: authError } = await supabase.auth.getSession();
            console.log('Sessão atual:', authData);
            console.log('Erro de sessão:', authError);

            // Testar operações diretas na tabela
            console.log('Testando acesso à tabela proprietarios...');
            const { data: testTable, error: testTableError } = await supabase
                .from('proprietarios')
                .select('id, nome, estado, cidade')
                .limit(1);
            console.log('Teste tabela - Data:', testTable);
            console.log('Teste tabela - Error:', testTableError);

            // Testar se podemos inserir (sem salvar)
            console.log('Testando estrutura de insert...');
            const testInsertData = {
                nome: 'TESTE_ESTRUTURA',
                estado: 'SP',
                cidade: 'TESTE',
                telefone: null,
                email: null
            };
            console.log('Dados de teste para insert:', testInsertData);

            console.log('=== FIM TESTE CONEXÃO ===');
        } catch (error) {
            console.error('Erro no teste de conexão:', error);
        }
    };

    const loadProprietarios = async () => {
        try {
            setLoading(true);
            setError('');

            console.log('=== DEBUG loadProprietarios ===');
            console.log('Iniciando carregamento de proprietários...');

            // Buscar proprietários do usuário logado
            const { data, error } = await supabase
                .from('proprietarios')
                .select(`
                    id,
                    nome,
                    estado,
                    cidade,
                    telefone,
                    email,
                    created_at,
                    updated_at
                `)
                .order('created_at', { ascending: false });

            console.log('=== RESULTADO QUERY ===');
            console.log('Data:', data);
            console.log('Error:', error);
            console.log('Data length:', data?.length);

            if (error) {
                console.error('Erro ao carregar proprietários:', error);
                console.error('Detalhes do erro:', {
                    message: error.message,
                    details: error.details,
                    hint: error.hint,
                    code: error.code
                });
                setError('Erro ao carregar proprietários. Verifique sua conexão.');
                return;
            }

            // Processar dados para incluir contagem de imóveis
            const proprietariosComContagem = (data || []).map(proprietario => ({
                ...proprietario,
                imoveis_count: 0 // Por enquanto, sempre 0
            }));

            console.log('Proprietários processados:', proprietariosComContagem);
            setProprietarios(proprietariosComContagem);
            console.log('=== FIM DEBUG loadProprietarios ===');
        } catch (error) {
            console.error('Erro ao carregar proprietários:', error);
            if (error instanceof Error) {
                console.error('Stack trace:', error.stack);
            }
            setError('Erro inesperado ao carregar proprietários.');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        console.log('=== DEBUG INICIO handleSubmit ===');
        console.log('FormData:', formData);
        console.log('EditingProprietario:', editingProprietario);

        // Validação client-side
        if (!formData.nome.trim()) {
            setError('Nome do proprietário é obrigatório');
            setIsSubmitting(false);
            return;
        }
        if (!formData.estado) {
            setError('Estado é obrigatório');
            setIsSubmitting(false);
            return;
        }
        if (!formData.cidade.trim()) {
            setError('Cidade é obrigatória');
            setIsSubmitting(false);
            return;
        }

        try {
            if (editingProprietario) {
                console.log('=== MODO ATUALIZAÇÃO ===');

                // Atualizar proprietário existente
                const { data, error } = await supabase
                    .from('proprietarios')
                    .update({
                        nome: formData.nome.trim(),
                        estado: formData.estado,
                        cidade: formData.cidade.trim(),
                        telefone: formData.telefone.trim() || null,
                        email: formData.email.trim() || null,
                        updated_at: new Date().toISOString()
                    })
                    .eq('id', editingProprietario.id)
                    .select();

                if (error) {
                    console.error('Erro ao atualizar proprietário:', error);
                    showError(
                        'Erro ao atualizar proprietário',
                        error.message || 'Verifique sua conexão e tente novamente.'
                    );
                    setIsSubmitting(false);
                    return;
                }

                if (!data || data.length === 0) {
                    showError(
                        'Erro ao atualizar',
                        'Não foi possível atualizar o proprietário. Verifique se você tem permissão.'
                    );
                    setIsSubmitting(false);
                    return;
                }

                showSuccess(
                    'Proprietário atualizado!',
                    `${formData.nome} foi atualizado com sucesso.`
                );
            } else {
                console.log('=== MODO CRIAÇÃO ===');

                // Criar novo proprietário
                const { data, error } = await supabase
                    .from('proprietarios')
                    .insert({
                        nome: formData.nome.trim(),
                        estado: formData.estado,
                        cidade: formData.cidade.trim(),
                        telefone: formData.telefone.trim() || null,
                        email: formData.email.trim() || null,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    })
                    .select();

                if (error) {
                    console.error('Erro ao criar proprietário:', error);
                    showError(
                        'Erro ao criar proprietário',
                        error.message || 'Verifique sua conexão e tente novamente.'
                    );
                    setIsSubmitting(false);
                    return;
                }

                if (!data || data.length === 0) {
                    showError(
                        'Erro ao criar',
                        'Não foi possível criar o proprietário. Verifique se você está logado.'
                    );
                    setIsSubmitting(false);
                    return;
                }

                showSuccess(
                    'Proprietário criado!',
                    `${formData.nome} foi adicionado com sucesso.`
                );
            }

            // Recarregar dados e fechar modal
            await loadProprietarios();
            closeModal();
            console.log('=== DEBUG FIM handleSubmit ===');
        } catch (error) {
            console.error('=== ERRO GERAL ===');
            console.error('Erro ao salvar proprietário:', error);
            showError(
                'Erro inesperado',
                'Ocorreu um erro inesperado. Tente novamente em alguns instantes.'
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (proprietario: Proprietario) => {
        console.log('=== DEBUG handleEdit ===');
        console.log('Proprietário selecionado:', proprietario);
        console.log('Dados do proprietário:', {
            id: proprietario.id,
            nome: proprietario.nome,
            estado: proprietario.estado,
            cidade: proprietario.cidade,
            telefone: proprietario.telefone,
            email: proprietario.email
        });

        setEditingProprietario(proprietario);
        setViewingProprietario(null);
        const formDataToSet = {
            nome: proprietario.nome,
            estado: proprietario.estado,
            cidade: proprietario.cidade,
            telefone: proprietario.telefone || '',
            email: proprietario.email || ''
        };

        console.log('FormData a ser definido:', formDataToSet);
        setFormData(formDataToSet);
        setShowModal(true);
        console.log('=== FIM DEBUG handleEdit ===');
    };

    const handleView = (proprietario: Proprietario) => {
        setViewingProprietario(proprietario);
        setEditingProprietario(null);
        setShowModal(true);
    };

    const handleDelete = async (id: string) => {
        const proprietario = proprietarios.find(p => p.id === id);
        const nomeProprietario = proprietario?.nome || 'este proprietário';

        if (!confirm(`Tem certeza que deseja excluir ${nomeProprietario}?`)) {
            return;
        }

        try {
            const { error } = await supabase
                .from('proprietarios')
                .delete()
                .eq('id', id);

            if (error) {
                console.error('Erro ao excluir proprietário:', error);
                showError(
                    'Erro ao excluir',
                    'Não foi possível excluir o proprietário. Tente novamente.'
                );
                return;
            }

            showSuccess(
                'Proprietário excluído!',
                `${nomeProprietario} foi removido com sucesso.`
            );
            loadProprietarios();
        } catch (error) {
            console.error('Erro ao excluir proprietário:', error);
            showError(
                'Erro inesperado',
                'Ocorreu um erro ao excluir o proprietário.'
            );
        }
    };

    const openCreateModal = () => {
        setEditingProprietario(null);
        setViewingProprietario(null);
        setFormData({
            nome: '',
            estado: '',
            cidade: '',
            telefone: '',
            email: ''
        });
        setShowModal(true);
    };

    const closeModal = () => {
        if (isSubmitting) return; // Não permite fechar durante submit

        setIsModalAnimating(false);
        setTimeout(() => {
            setShowModal(false);
            setEditingProprietario(null);
            setViewingProprietario(null);
            setFormData({
                nome: '',
                estado: '',
                cidade: '',
                telefone: '',
                email: ''
            });
            setError('');
        }, 200); // Tempo da animação de saída
    };

    const filteredProprietarios = proprietarios.filter(proprietario =>
        proprietario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proprietario.cidade.toLowerCase().includes(searchTerm.toLowerCase()) ||
        proprietario.estado.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Proprietários</h1>
                    <p className="text-gray-600">Gerencie os proprietários dos imóveis</p>
                </div>
                <Button
                    variant="btn_add"
                    onClick={openCreateModal}
                    className="flex items-center gap-2"
                >
                    <Plus className="h-4 w-4" />
                    Adicionar Proprietário
                </Button>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <span className="text-red-800">{error}</span>
                </div>
            )}


            {/* Estatísticas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <User className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total de Proprietários</p>
                            <p className="text-2xl font-bold text-gray-900">{proprietarios.length}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <Building className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total de Imóveis</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {proprietarios.reduce((total, p) => total + (p.imoveis_count || 0), 0)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filtros */}
            <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
                <div className="flex gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Buscar por nome, cidade ou estado..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Lista de Proprietários */}
            <div className="bg-white rounded-lg shadow-sm border">
                {filteredProprietarios.length === 0 ? (
                    <div className="text-center py-12">
                        <User className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum proprietário encontrado</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece adicionando um novo proprietário.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Proprietário
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Localização
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Contato
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Imóveis
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredProprietarios.map((proprietario) => (
                                    <tr key={proprietario.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 flex-shrink-0">
                                                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                        <User className="h-5 w-5 text-blue-600" />
                                                    </div>
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {proprietario.nome}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center text-sm text-gray-900">
                                                <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                                                {proprietario.cidade}, {proprietario.estado}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="space-y-1">
                                                {proprietario.telefone && (
                                                    <div className="flex items-center text-sm text-gray-900">
                                                        <Phone className="h-4 w-4 text-gray-400 mr-2" />
                                                        {proprietario.telefone}
                                                    </div>
                                                )}
                                                {proprietario.email && (
                                                    <div className="flex items-center text-sm text-gray-900">
                                                        <Mail className="h-4 w-4 text-gray-400 mr-2" />
                                                        {proprietario.email}
                                                    </div>
                                                )}
                                                {!proprietario.telefone && !proprietario.email && (
                                                    <span className="text-sm text-gray-500">Sem contato</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                {proprietario.imoveis_count || 0} imóveis
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleView(proprietario)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="Visualizar"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(proprietario)}
                                                    className="text-indigo-600 hover:text-indigo-900"
                                                    title="Editar"
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(proprietario.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                    title="Excluir"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop com blur */}
                    <div
                        className={`absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-all duration-300 ${isModalAnimating ? 'opacity-100' : 'opacity-0'
                            }`}
                        onClick={closeModal}
                    />

                    {/* Modal com animações */}
                    <div className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md transform transition-all duration-300 ${isModalAnimating
                        ? 'scale-100 opacity-100 translate-y-0'
                        : 'scale-95 opacity-0 translate-y-4'
                        }`}>
                        <div className="p-6">
                            {/* Header do Modal */}
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {viewingProprietario ? 'Visualizar Proprietário' :
                                            editingProprietario ? 'Editar Proprietário' : 'Adicionar Proprietário'}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                        {viewingProprietario ? 'Informações do proprietário' :
                                            editingProprietario ? 'Atualize as informações abaixo' : 'Preencha os dados do novo proprietário'}
                                    </p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                    disabled={isSubmitting}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {viewingProprietario ? (
                                // Visualização
                                <div className="space-y-6">
                                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                                            <User className="h-4 w-4" />
                                            Informações Pessoais
                                        </h4>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Nome</label>
                                                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{viewingProprietario.nome}</p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Estado</label>
                                                    <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{viewingProprietario.estado}</p>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cidade</label>
                                                    <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{viewingProprietario.cidade}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {(viewingProprietario.telefone || viewingProprietario.email) && (
                                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                                            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                                                <Phone className="h-4 w-4" />
                                                Contato
                                            </h4>
                                            <div className="space-y-3">
                                                {viewingProprietario.telefone && (
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Telefone</label>
                                                        <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{viewingProprietario.telefone}</p>
                                                    </div>
                                                )}
                                                {viewingProprietario.email && (
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">E-mail</label>
                                                        <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{viewingProprietario.email}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                                            <Building className="h-4 w-4" />
                                            Estatísticas
                                        </h4>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total de Imóveis</label>
                                            <p className="mt-1 text-lg font-bold text-blue-600 dark:text-blue-400">{viewingProprietario.imoveis_count || 0} imóveis</p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                // Formulário
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                Nome do Proprietário <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.nome}
                                                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="Digite o nome completo"
                                                required
                                                disabled={isSubmitting}
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Estado <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={formData.estado}
                                                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                    required
                                                    disabled={isSubmitting}
                                                >
                                                    <option value="">Selecione o estado</option>
                                                    {estadosBrasil.map(estado => (
                                                        <option key={estado} value={estado}>{estado}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                    Cidade <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    value={formData.cidade}
                                                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                                                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                    placeholder="Digite a cidade"
                                                    required
                                                    disabled={isSubmitting}
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Telefone</label>
                                            <input
                                                type="tel"
                                                value={formData.telefone}
                                                onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="(00) 00000-0000"
                                                disabled={isSubmitting}
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">E-mail</label>
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                                placeholder="email@exemplo.com"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    </div>

                                    {/* Footer do Modal */}
                                    <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                            disabled={isSubmitting}
                                        >
                                            Cancelar
                                        </button>
                                        <Button
                                            type="submit"
                                            variant="btn_add"
                                            disabled={isSubmitting}
                                            className="px-6 py-2"
                                        >
                                            {isSubmitting ? (
                                                <div className="flex items-center gap-2">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                    {editingProprietario ? 'Atualizando...' : 'Criando...'}
                                                </div>
                                            ) : (
                                                editingProprietario ? 'Atualizar' : 'Criar'
                                            )}
                                        </Button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Container */}
            <ToastContainer toasts={toasts} />
        </div>
    );
};

export default Proprietarios;
